import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { kJsonObject } from "../../../Admin/kReusableCoreTS/kJsonObject";
import { kPhoneNumberCore } from "../../../Admin/kReusableCoreTS/kPhoneNumberCore";
import { AppIcons } from "./AppIcons";

// AppNewChat - "NEW CHAT" (2026-10-14, sub-agent `chatactions`): the owner types a phone number, the service asks WhatsApp
// once whether it is on WhatsApp (`ChatActions.Check` - `onWhatsApp`, rc14 `Socket/socket.js:222-245`; bounded there:
// one at a time, a daily cap, nothing in the first 10 minutes after connecting, answers cached a week), and a number that
// is on WhatsApp opens the EXISTING provisional new-chat route `#n/<chat id>` with the composer.
// ⛔ DECISION 9: nothing here sends a message. The first message is still the owner's own press of Send in that chat.
// Pure rules + markup (habitat-free); the browser half is `AppMain`'s wiring (one submit listener on the form).

export class AppNewChatAnswer {
    public Ok: boolean = false;
    public ChatId: string = "";
    public Why: string = "";
    /** No answer at all (status 0, a 5xx, not JSON). */
    public Lost: boolean = false;
}

export class AppNewChatRules {
    public static readonly Route: string = "newchat";
    public static readonly FormId: string = "wac-nc-form";
    public static readonly InputId: string = "wac-nc-number";
    public static readonly SayId: string = "wac-nc-say";
    public static readonly GoId: string = "wac-nc-go";
    public static readonly NumberWhy: string = "Include the country code, e.g. +46 70 123 45 67.";
    public static readonly LostWhy: string = "Couldn't reach WACommander to check the number. Check your connection and try again.";
    public static readonly CheckingWords: string = "Checking with WhatsApp…";
    /** Chats this visit opened from here: the provisional page may have no message yet (an empty conversation is drawn). */
    public static readonly Started: Set<string> = new Set<string>();

    /** The chat id for what was typed: "<digits>@s.whatsapp.net", or "" when it is not a full international number. */
    public static ChatIdOf = (Typed: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const E164: string = kPhoneNumberCore.ToE164(nz(Typed).trim(), "");
            if (kPhoneNumberCore.IsE164(E164) === false) { return ""; }
            return E164.substring(1) + "@s.whatsapp.net";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppNewChatRules.ChatIdOf");
            return "";
        }
    };

    /** The check's body: the existing `/chat/settings` door, action `check`, the owner's proof. */
    public static Body = (Number: string, Key: string, ChatId: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return JSON.stringify({ number: Number, key: Key, chatId: ChatId, action: "set", setting: "check", value: true });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppNewChatRules.Body");
            return "";
        }
    };

    public static Parse = (Status: number, Text: string): AppNewChatAnswer => {
        const Out: AppNewChatAnswer = new AppNewChatAnswer();
        if (getCoreUtils().KErrorMode === true) { Out.Lost = true; return Out; }
        try {
            const Doc: Record<string, unknown> | null = kJsonObject.ObjectOfJson(Text);
            if (Status === 0 || Status >= 500 || Doc === null || typeof Doc.ok !== "boolean") { Out.Lost = true; Out.Why = AppNewChatRules.LostWhy; return Out; }
            if (typeof Doc.why === "string") { Out.Why = Doc.why; }
            if (Doc.ok === true && Doc.onWhatsApp === true && typeof Doc.chatId === "string" && /^\d{6,15}@s\.whatsapp\.net$/.test(Doc.chatId)) {
                Out.Ok = true; Out.ChatId = Doc.chatId; return Out;
            }
            if (Out.Why.length === 0) { Out.Why = "That number could not be checked just now."; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppNewChatRules.Parse");
            Out.Lost = true; return Out;
        }
    };

    /** The form. `Typed` stays in the box; `Say` is the one line under it (ink, a live region). */
    public static Html = (Typed: string, Say: string, Busy: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Disabled: string = ""; if (Busy === true) { Disabled = " aria-disabled=\"true\""; }
            let Words: string = "Open"; if (Busy === true) { Words = AppNewChatRules.CheckingWords; }
            return "<section class=\"newchat panel\" aria-labelledby=\"wac-nc-h\"><h2 id=\"wac-nc-h\">" + AppIcons.Svg(AppIcons.NewChat, 20)
                + "<span>New chat</span></h2><form id=\"" + AppNewChatRules.FormId + "\" novalidate><label for=\"" + AppNewChatRules.InputId
                + "\">Phone number</label><input id=\"" + AppNewChatRules.InputId + "\" type=\"tel\" inputmode=\"tel\" autocomplete=\"tel\" value=\""
                + kHTMLCore.EscapeAttr(Typed) + "\" aria-describedby=\"" + AppNewChatRules.SayId + "\"><button type=\"submit\" class=\"btn\" id=\""
                + AppNewChatRules.GoId + "\" title=\"Checks the number with WhatsApp, then opens the chat. Nothing is sent.\"" + Disabled + ">" + kHTMLCore.EscapeHtml(Words) + "</button></form><p class=\"ncsay\" id=\""
                + AppNewChatRules.SayId + "\" role=\"status\">" + kHTMLCore.EscapeHtml(Say) + "</p></section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppNewChatRules.Html");
            return "";
        }
    };

    /** A conversation with no message yet, drawn in the frame like any page (the frame's own sheets style it). */
    public static EmptyPage = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<!doctype html><html lang=\"en\"><head><meta charset=\"utf-8\"><title>" + kHTMLCore.EscapeHtml(Title)
                + "</title></head><body><main class=\"thread\"><p class=\"empty\">No messages yet. Write the first one below.</p>"
                + "<div class=\"chat\"></div></main></body></html>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppNewChatRules.EmptyPage");
            return "";
        }
    };
}
